import * as React from 'react';
import AssignmentLateOutlined from '@material-ui/icons/AssignmentLateOutlined';
import { fetchExpiredQuestions, fetchExpiringQuestions } from 'slices/questions';
import { IExpiringQuestion } from 'interfaces/questions';
import { PendingOutlinedIcon } from '../../PendingOutlinedIcon';
import { ToDoChild, ToDoDescriptor } from '../types';
import { uniqBy } from 'lodash';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

export const useExpiringQuestionsToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { expiredQuestions, expiringQuestions, isFetching, error } = useTypedSelector(state => state.questions);

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchExpiredQuestions(organizationId));
      dispatch(fetchExpiringQuestions(organizationId));
    }
  }, [organizationId, dispatch]);

  const hasError = !!error;

  const toChildren = (questions: IExpiringQuestion[]): ToDoChild[] =>
    uniqBy(questions, 'sectionId').map(question => {
      const questionsCount = questions.filter(item => item.sectionId === question.sectionId).length;
      return {
        id: question.questionId,
        href: `/organizations/${organizationId}/questionnaire-v2/${question.sectionId}`,
        label: `${question.sectionName} (${questionsCount})`
      };
    });

  return [
    {
      id: 'questions.expired',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiredAnswers', 'Update expired answers'),
      icon: <AssignmentLateOutlined />,
      count: expiredQuestions.length,
      children: toChildren(expiredQuestions),
      isVisible: expiredQuestions.length > 0 && !hasError,
      isFetching
    },
    {
      id: 'questions.expiring',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiringAnswers', 'Update expiring answers'),
      icon: <PendingOutlinedIcon />,
      count: expiringQuestions.length,
      children: toChildren(expiringQuestions),
      isVisible: expiringQuestions.length > 0 && !hasError,
      isFetching
    }
  ];
};
